import { useState } from 'react';
import { useCRMAuth } from '../../context/CRMAuthContext';
import axios from 'axios';
import { Building2, CreditCard, Users, Bell, Shield, ExternalLink, Check, Zap } from 'lucide-react';
import { toast } from 'sonner';
import CRMLayout from '../../components/crm/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const plans = [
  { id: 'starter', name: 'Starter', price: 19, users: 5, current: false },
  { id: 'growth', name: 'Growth', price: 49, users: 20, current: false },
  { id: 'business', name: 'Business', price: 99, users: 'Unlimited', current: false }
];

const CRMSettings = () => {
  const { company, user, token } = useCRMAuth();
  const [activeTab, setActiveTab] = useState('company');

  const openBillingPortal = async () => {
    try {
      const response = await axios.post(`${API}/api/crm/subscription/create-portal-session?company_id=${company.company_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error('Unable to open billing portal');
    }
  };

  const tabs = [
    { id: 'company', label: 'Company', icon: Building2 },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  return (
    <CRMLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-slate-400">Manage your company settings and subscription</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700/50 pb-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Company Tab */}
        {activeTab === 'company' && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Company Information</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Company Name</label>
                  <p className="text-white font-medium">{company?.name}</p>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Email</label>
                  <p className="text-white">{company?.email}</p>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Company ID</label>
                  <p className="text-slate-400 text-sm font-mono">{company?.company_id}</p>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Created</label>
                  <p className="text-white">{company?.created_at ? new Date(company.created_at).toLocaleDateString() : '-'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* Current Plan */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">Current Plan</h3>
                  <p className="text-slate-400">Your subscription details</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  company?.subscription_status === 'active' ? 'bg-green-500/10 text-green-400' :
                  company?.subscription_status === 'trial' ? 'bg-yellow-500/10 text-yellow-400' :
                  'bg-red-500/10 text-red-400'
                }`}>
                  {company?.subscription_status}
                </span>
              </div>
              
              <div className="flex items-center gap-6 p-4 bg-slate-900/50 rounded-xl">
                <div className="p-3 bg-blue-500/10 rounded-xl">
                  <Zap className="w-8 h-8 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-white capitalize">{company?.subscription_plan} Plan</h4>
                  <p className="text-slate-400">Up to {company?.max_users === 9999 ? 'Unlimited' : company?.max_users} team members</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">
                    ${plans.find(p => p.id === company?.subscription_plan)?.price || 0}
                    <span className="text-sm text-slate-400 font-normal">/month</span>
                  </p>
                </div>
              </div>

              {company?.subscription_ends_at && (
                <p className="text-sm text-slate-400 mt-4">
                  {company.subscription_status === 'trial' ? 'Trial ends' : 'Next billing'}: {new Date(company.subscription_ends_at).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Plan Options */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Available Plans</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {plans.map(plan => {
                  const isCurrent = plan.id === company?.subscription_plan;
                  return (
                    <div key={plan.id} className={`p-4 rounded-xl border ${isCurrent ? 'border-blue-500/50 bg-blue-500/5' : 'border-slate-700/50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-white">{plan.name}</h4>
                        {isCurrent && <Check className="w-4 h-4 text-blue-400" />}
                      </div>
                      <p className="text-2xl font-bold text-white">${plan.price}<span className="text-sm text-slate-400 font-normal">/mo</span></p>
                      <p className="text-sm text-slate-400 mt-1">Up to {plan.users} users</p>
                    </div>
                  );
                })}
              </div>
              
              {company?.stripe_customer_id && (
                <button
                  onClick={openBillingPortal}
                  className="mt-6 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Manage Subscription
                </button>
              )}
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Notification Preferences</h3>
            <div className="space-y-4">
              {[
                { label: 'New lead notifications', desc: 'Get notified when a new lead is added' },
                { label: 'Deal stage changes', desc: 'Get notified when deals move through the pipeline' },
                { label: 'Weekly summary', desc: 'Receive a weekly report of CRM activity' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{item.label}</p>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-slate-700 rounded-full peer peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CRMLayout>
  );
};

export default CRMSettings;
